import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { SidebarToggleButton, useBrandTokens } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { usePowerSyncQuery, usePowerSyncDb } from '../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  generateSlotDrafts,
  qAvailabilityRules,
  qAppointmentSlotsInRange,
  qAppointmentProviders,
  qAppointmentServicesWithCategory,
  mapAvailabilityRuleRowToUI,
  mapUIToAvailabilityRuleColumns,
  type AppointmentAvailabilityRuleRow,
  type AppointmentSlotWithBookingRow,
  type AppointmentProviderRow,
  type AppointmentServiceWithCategoryRow,
  type AvailabilityRuleUI,
  type GeneratedSlotDraft,
  type SlotStatus,
} from '@helios/data-access';
import { AvailabilityRuleDrawer } from './AvailabilityRuleDrawer';
import { GenerateSlotsModal } from './GenerateSlotsModal';

// ─── Constants ───

type Tab = 'rules' | 'slots';

const DAY_SHORT: Record<number, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  // Slot timestamps are wall-clock-as-UTC — read in UTC so display matches clinic time.
  return d.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

function dayShortFromIsoDate(date: string | null): string {
  if (!date) return '';
  const d = new Date(`${date}T00:00:00`);
  return DAY_SHORT[d.getDay()] ?? '';
}

function deriveDisplayStatus(
  status: string | null,
  capacity: number | null,
  booked: number | null,
): { label: string; color: string; bg: string } {
  if (status === 'BLOCKED') return { label: 'Blocked', color: '#92400e', bg: '#fef3c7' };
  if (status === 'CANCELLED') return { label: 'Cancelled', color: '#7f1d1d', bg: '#fee2e2' };
  const cap = capacity ?? 1;
  const used = booked ?? 0;
  if (used >= cap) return { label: 'Full', color: '#9a3412', bg: '#ffedd5' };
  return { label: 'Available', color: '#15803d', bg: '#dcfce7' };
}

// ─── Page ───

export function ProviderAvailabilityPage() {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { collapsed, onToggleSidebar } = useAppointmentsContext();

  // ── Data ──
  const { data: ruleRows = [] } =
    usePowerSyncQuery<AppointmentAvailabilityRuleRow>(qAvailabilityRules());
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: serviceRows = [] } = usePowerSyncQuery<AppointmentServiceWithCategoryRow>(
    qAppointmentServicesWithCategory(),
  );

  const rules = useMemo(() => ruleRows.map(mapAvailabilityRuleRowToUI), [ruleRows]);

  const providerById = useMemo(() => new Map(providerRows.map((p) => [p.id, p])), [providerRows]);
  const serviceById = useMemo(() => new Map(serviceRows.map((s) => [s.id, s])), [serviceRows]);

  // ── UI state ──
  const [tab, setTab] = useState<Tab>('rules');

  // Rule drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AvailabilityRuleUI | null>(null);

  // Generate slots modal
  const [generateRule, setGenerateRule] = useState<AvailabilityRuleUI | null>(null);

  // Deactivate confirm
  const [deactivateTarget, setDeactivateTarget] = useState<AvailabilityRuleUI | null>(null);

  // Slots tab filters
  const [slotStartDate, setSlotStartDate] = useState(todayStr());
  const [slotEndDate, setSlotEndDate] = useState(addDaysIso(todayStr(), 14));
  const [providerFilter, setProviderFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<SlotStatus | ''>('');

  const { data: slotRows = [] } = usePowerSyncQuery<AppointmentSlotWithBookingRow>(
    qAppointmentSlotsInRange(slotStartDate, slotEndDate),
  );

  const filteredSlots = useMemo(() => {
    return slotRows.filter((s) => {
      if (providerFilter && s.provider_id !== providerFilter) return false;
      if (serviceFilter && s.service_id !== serviceFilter) return false;
      if (statusFilter && s.status !== statusFilter) return false;
      return true;
    });
  }, [slotRows, providerFilter, serviceFilter, statusFilter]);

  // ── Handlers ──
  const handleOpenNewRule = () => {
    setEditingRule(null);
    setDrawerOpen(true);
  };

  const handleEditRule = (rule: AvailabilityRuleUI) => {
    setEditingRule(rule);
    setDrawerOpen(true);
  };

  const insertSlotsForRule = useCallback(
    async (
      rule: AvailabilityRuleUI,
      ruleId: string,
      drafts: GeneratedSlotDraft[],
      skipExisting: boolean,
    ): Promise<{ created: number; skipped: number }> => {
      if (!db) return { created: 0, skipped: 0 };
      // Look up existing slots in range for this rule's provider+service to skip duplicates.
      const dateSet = new Set(drafts.map((d) => d.slotDate));
      const dates = [...dateSet].sort();
      let existing: Array<{ start_time: string; end_time: string }> = [];
      if (skipExisting && dates.length > 0) {
        const placeholders = dates.map(() => '?').join(',');
        const res = await db.execute(
          `SELECT start_time, end_time FROM appointment_slots
            WHERE deleted_at IS NULL AND provider_id = ? AND service_id = ?
              AND slot_date IN (${placeholders})`,
          [rule.providerId, rule.serviceId, ...dates],
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const r = res.rows as any;
        existing = (r?._array ?? r ?? []) as Array<{ start_time: string; end_time: string }>;
      }
      const existingKeys = new Set(existing.map((r) => `${r.start_time}|${r.end_time}`));

      const now = new Date().toISOString();
      let created = 0;
      let skipped = 0;
      for (const draft of drafts) {
        const key = `${draft.startTime}|${draft.endTime}`;
        if (existingKeys.has(key)) {
          skipped++;
          continue;
        }
        await db.execute(
          `INSERT INTO appointment_slots (
            id, org_id, clinic_id, availability_rule_id, provider_id, service_id,
            slot_date, start_time, end_time, capacity, slot_type, status, source,
            notes, created_at, updated_at, deleted_at
          ) VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?, 'AVAILABLE', 'AUTO', NULL, ?, ?, NULL)`,
          [
            crypto.randomUUID(),
            orgId,
            ruleId,
            rule.providerId,
            rule.serviceId,
            draft.slotDate,
            draft.startTime,
            draft.endTime,
            rule.capacityPerSlot,
            rule.slotType,
            now,
            now,
          ],
        );
        created++;
      }
      return { created, skipped };
    },
    [db, orgId],
  );

  const handleSaveRule = useCallback(
    async (data: Omit<AvailabilityRuleUI, 'id'>) => {
      if (!db) return;
      const now = new Date().toISOString();
      const cols = mapUIToAvailabilityRuleColumns(data);
      let ruleId: string;
      if (editingRule) {
        ruleId = editingRule.id;
        await db.execute(
          `UPDATE appointment_availability_rules SET
            name = ?, provider_id = ?, service_id = ?, days_of_week = ?,
            start_time = ?, end_time = ?, slot_duration_minutes = ?,
            capacity_per_slot = ?, slot_type = ?, effective_start_date = ?,
            effective_end_date = ?, auto_generate = ?, generate_days_ahead = ?,
            notes = ?, is_active = ?, updated_at = ?
          WHERE id = ?`,
          [
            cols.name,
            cols.provider_id,
            cols.service_id,
            cols.days_of_week,
            cols.start_time,
            cols.end_time,
            cols.slot_duration_minutes,
            cols.capacity_per_slot,
            cols.slot_type,
            cols.effective_start_date,
            cols.effective_end_date,
            cols.auto_generate,
            cols.generate_days_ahead,
            cols.notes,
            cols.is_active,
            now,
            ruleId,
          ],
        );
      } else {
        ruleId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO appointment_availability_rules (
            id, org_id, clinic_id, name, provider_id, service_id, days_of_week,
            start_time, end_time, slot_duration_minutes, capacity_per_slot,
            slot_type, effective_start_date, effective_end_date, auto_generate,
            generate_days_ahead, notes, is_active, created_at, updated_at, deleted_at
          ) VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            ruleId,
            orgId,
            cols.name,
            cols.provider_id,
            cols.service_id,
            cols.days_of_week,
            cols.start_time,
            cols.end_time,
            cols.slot_duration_minutes,
            cols.capacity_per_slot,
            cols.slot_type,
            cols.effective_start_date,
            cols.effective_end_date,
            cols.auto_generate,
            cols.generate_days_ahead,
            cols.notes,
            cols.is_active,
            now,
            now,
          ],
        );

        // Auto-generate slots when enabled.
        if (data.autoGenerate && data.generateDaysAhead > 0) {
          const start = todayStr();
          const end = addDaysIso(start, data.generateDaysAhead);
          const drafts = generateSlotDrafts({
            rule: { ...data, id: ruleId },
            startDate: start,
            endDate: end,
          });
          await insertSlotsForRule({ ...data, id: ruleId }, ruleId, drafts, true);
        }
      }
      setDrawerOpen(false);
      setEditingRule(null);
    },
    [db, editingRule, orgId, insertSlotsForRule],
  );

  const handleDuplicateRule = useCallback(
    async (rule: AvailabilityRuleUI) => {
      if (!db) return;
      const now = new Date().toISOString();
      const cols = mapUIToAvailabilityRuleColumns({
        ...rule,
        name: `${rule.name} (Copy)`,
      });
      await db.execute(
        `INSERT INTO appointment_availability_rules (
          id, org_id, clinic_id, name, provider_id, service_id, days_of_week,
          start_time, end_time, slot_duration_minutes, capacity_per_slot,
          slot_type, effective_start_date, effective_end_date, auto_generate,
          generate_days_ahead, notes, is_active, created_at, updated_at, deleted_at
        ) VALUES (?, ?, NULL, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
        [
          crypto.randomUUID(),
          orgId,
          cols.name,
          cols.provider_id,
          cols.service_id,
          cols.days_of_week,
          cols.start_time,
          cols.end_time,
          cols.slot_duration_minutes,
          cols.capacity_per_slot,
          cols.slot_type,
          cols.effective_start_date,
          cols.effective_end_date,
          cols.auto_generate,
          cols.generate_days_ahead,
          cols.notes,
          cols.is_active,
          now,
          now,
        ],
      );
    },
    [db, orgId],
  );

  const handleDeactivateConfirm = useCallback(async () => {
    if (!db || !deactivateTarget) return;
    await db.execute(
      `UPDATE appointment_availability_rules SET is_active = 0, updated_at = ? WHERE id = ?`,
      [new Date().toISOString(), deactivateTarget.id],
    );
    setDeactivateTarget(null);
  }, [db, deactivateTarget]);

  const handleGenerateForRule = useCallback(
    async ({ drafts, skipExisting }: { drafts: GeneratedSlotDraft[]; skipExisting: boolean }) => {
      if (!generateRule) return;
      return insertSlotsForRule(generateRule, generateRule.id, drafts, skipExisting);
    },
    [generateRule, insertSlotsForRule],
  );

  const handleSlotAction = useCallback(
    async (slotId: string, action: 'block' | 'unblock' | 'cancel' | 'delete') => {
      if (!db) return;
      const now = new Date().toISOString();
      if (action === 'delete') {
        // Only delete if no appointment references this slot (UI guard; the
        // table is otherwise soft-deletable via deleted_at).
        const used = await db.execute(
          `SELECT id FROM appointments WHERE appointment_slot_id = ? LIMIT 1`,
          [slotId],
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const u = used.rows as any;
        const rows = (u?._array ?? u ?? []) as unknown[];
        if (rows.length > 0) return;
        await db.execute(
          `UPDATE appointment_slots SET deleted_at = ?, updated_at = ? WHERE id = ?`,
          [now, now, slotId],
        );
        return;
      }
      const status =
        action === 'block' ? 'BLOCKED' : action === 'unblock' ? 'AVAILABLE' : 'CANCELLED';
      await db.execute(`UPDATE appointment_slots SET status = ?, updated_at = ? WHERE id = ?`, [
        status,
        now,
        slotId,
      ]);
    },
    [db],
  );

  // ── Slot generation existing-key lookup for modal preview ──
  const existingKeyCount = useCallback(
    (drafts: GeneratedSlotDraft[]) => {
      if (!generateRule || drafts.length === 0) return 0;
      const candidates = new Set(drafts.map((d) => `${d.startTime}|${d.endTime}`));
      let n = 0;
      for (const s of slotRows) {
        if (s.provider_id !== generateRule.providerId) continue;
        if (s.service_id !== generateRule.serviceId) continue;
        if (!s.start_time || !s.end_time) continue;
        if (candidates.has(`${s.start_time}|${s.end_time}`)) n++;
      }
      return n;
    },
    [generateRule, slotRows],
  );

  // ── Render ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {onToggleSidebar && (
            <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              <h1
                style={{
                  fontSize: 'var(--font-size-xl2)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-xl2)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                  margin: 0,
                }}
              >
                Provider Availability
              </h1>
              <span
                style={{
                  height: 22,
                  padding: '0 var(--sp-2)',
                  borderRadius: 'var(--radius-xs)',
                  background: 'var(--color-warning-bg)',
                  color: 'var(--color-warning-text)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: '22px',
                }}
              >
                Admin only
              </span>
            </div>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '2px 0 0',
              }}
            >
              Define provider availability so PowerCareAI can generate bookable appointment slots.
            </p>
          </div>
        </div>

        {tab === 'rules' && (
          <Button variant="gradient" size="md" onClick={handleOpenNewRule}>
            + Add Availability
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div
        style={{
          padding: 'var(--sp-2) var(--sp-6) 0',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: 'var(--sp-2)',
          flexShrink: 0,
        }}
      >
        <TabButton active={tab === 'rules'} onClick={() => setTab('rules')}>
          Availability Rules
        </TabButton>
        <TabButton active={tab === 'slots'} onClick={() => setTab('slots')}>
          Generated Slots
        </TabButton>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4) var(--sp-6)' }}>
        {tab === 'rules' ? (
          <RulesTable
            rules={rules}
            providerById={providerById}
            serviceById={serviceById}
            onEdit={handleEditRule}
            onDuplicate={handleDuplicateRule}
            onGenerate={(r) => setGenerateRule(r)}
            onDeactivate={(r) => setDeactivateTarget(r)}
          />
        ) : (
          <SlotsTable
            slots={filteredSlots}
            providerById={providerById}
            serviceById={serviceById}
            startDate={slotStartDate}
            endDate={slotEndDate}
            onStartDateChange={setSlotStartDate}
            onEndDateChange={setSlotEndDate}
            providerFilter={providerFilter}
            serviceFilter={serviceFilter}
            statusFilter={statusFilter}
            onProviderFilterChange={setProviderFilter}
            onServiceFilterChange={setServiceFilter}
            onStatusFilterChange={setStatusFilter}
            providers={providerRows}
            services={serviceRows}
            onSlotAction={handleSlotAction}
          />
        )}
      </div>

      {/* Drawers / Modals */}
      <AvailabilityRuleDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        brandTokens={brandTokens}
        rule={editingRule}
        providers={providerRows}
        services={serviceRows}
        onSave={handleSaveRule}
      />

      <GenerateSlotsModal
        open={!!generateRule}
        onOpenChange={(open) => {
          if (!open) setGenerateRule(null);
        }}
        brandTokens={brandTokens}
        rule={generateRule}
        existingKeyCount={existingKeyCount}
        onGenerate={handleGenerateForRule}
      />

      <Modal
        open={!!deactivateTarget}
        onOpenChange={(open) => {
          if (!open) setDeactivateTarget(null);
        }}
        title="Deactivate Availability"
        maxWidth={420}
        portalStyle={brandTokens as CSSProperties}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3)' }}>
            <Button variant="outline" size="md" onClick={() => setDeactivateTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              size="md"
              onClick={handleDeactivateConfirm}
              style={{ background: 'var(--color-danger)', color: '#fff' }}
            >
              Deactivate
            </Button>
          </div>
        }
      >
        <p style={{ color: 'var(--text)', fontSize: 'var(--font-size-base)', margin: 0 }}>
          Deactivate <strong>{deactivateTarget?.name}</strong>? Existing slots stay but new ones
          will not be auto-generated.
        </p>
      </Modal>
    </div>
  );
}

// ─── Tab Button ───

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 'var(--sp-3) var(--sp-4)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
        color: active ? 'var(--color-primary)' : 'var(--text-muted)',
        cursor: 'pointer',
        borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

// ─── Rules Table ───

function RulesTable({
  rules,
  providerById,
  serviceById,
  onEdit,
  onDuplicate,
  onGenerate,
  onDeactivate,
}: {
  rules: AvailabilityRuleUI[];
  providerById: Map<string, AppointmentProviderRow>;
  serviceById: Map<string, AppointmentServiceWithCategoryRow>;
  onEdit: (r: AvailabilityRuleUI) => void;
  onDuplicate: (r: AvailabilityRuleUI) => Promise<void>;
  onGenerate: (r: AvailabilityRuleUI) => void;
  onDeactivate: (r: AvailabilityRuleUI) => void;
}) {
  if (rules.length === 0) return <EmptyRulesState />;

  return (
    <div>
      {rules.map((r) => {
        const p = providerById.get(r.providerId);
        const s = serviceById.get(r.serviceId);
        return (
          <div
            key={r.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 1fr 1fr 1.2fr 0.8fr 0.8fr 0.8fr 1.4fr',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              padding: 'var(--sp-3) var(--sp-4)',
              borderBottom: '1px solid var(--border)',
              opacity: r.isActive ? 1 : 0.6,
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {r.name}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {r.slotType.replace('_', ' ').toLowerCase()}
              </div>
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              {p?.display_name || p?.full_name || '—'}
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>{s?.name ?? '—'}</div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              {r.daysOfWeek.map((d) => d.slice(0, 3)).join(', ') || '—'}
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              {r.startTime}–{r.endTime}
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>{r.slotDurationMinutes}m</div>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>{r.capacityPerSlot}/slot</div>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <Button variant="outline" size="sm" onClick={() => onEdit(r)}>
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => void onDuplicate(r)}>
                Duplicate
              </Button>
              <Button variant="outline" size="sm" onClick={() => onGenerate(r)}>
                Generate
              </Button>
              {r.isActive && (
                <Button variant="outline" size="sm" onClick={() => onDeactivate(r)}>
                  Deactivate
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyRulesState() {
  return (
    <div
      style={{
        padding: 'var(--sp-8)',
        textAlign: 'center',
        color: 'var(--text-muted)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text)',
        }}
      >
        No provider availability has been created yet.
      </div>
      <div style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--font-size-sm)' }}>
        Add availability so PowerCareAI can generate bookable appointment slots.
      </div>
    </div>
  );
}

// ─── Slots Table ───

function SlotsTable({
  slots,
  providerById,
  serviceById,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  providerFilter,
  serviceFilter,
  statusFilter,
  onProviderFilterChange,
  onServiceFilterChange,
  onStatusFilterChange,
  providers,
  services,
  onSlotAction,
}: {
  slots: AppointmentSlotWithBookingRow[];
  providerById: Map<string, AppointmentProviderRow>;
  serviceById: Map<string, AppointmentServiceWithCategoryRow>;
  startDate: string;
  endDate: string;
  onStartDateChange: (d: string) => void;
  onEndDateChange: (d: string) => void;
  providerFilter: string;
  serviceFilter: string;
  statusFilter: SlotStatus | '';
  onProviderFilterChange: (id: string) => void;
  onServiceFilterChange: (id: string) => void;
  onStatusFilterChange: (s: SlotStatus | '') => void;
  providers: AppointmentProviderRow[];
  services: AppointmentServiceWithCategoryRow[];
  onSlotAction: (
    slotId: string,
    action: 'block' | 'unblock' | 'cancel' | 'delete',
  ) => Promise<void>;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-4)',
          flexWrap: 'wrap',
        }}
      >
        <FilterDate label="From" value={startDate} onChange={onStartDateChange} />
        <FilterDate label="To" value={endDate} onChange={onEndDateChange} />
        <FilterSelect
          value={providerFilter}
          onChange={onProviderFilterChange}
          placeholder="All providers"
        >
          {providers
            .filter((p) => p.active == 1)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name || p.full_name}
              </option>
            ))}
        </FilterSelect>
        <FilterSelect
          value={serviceFilter}
          onChange={onServiceFilterChange}
          placeholder="All services"
        >
          {services
            .filter((s) => s.active == 1)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </FilterSelect>
        <FilterSelect
          value={statusFilter}
          onChange={(v) => onStatusFilterChange(v as SlotStatus | '')}
          placeholder="All statuses"
        >
          <option value="AVAILABLE">Available</option>
          <option value="BLOCKED">Blocked</option>
          <option value="CANCELLED">Cancelled</option>
        </FilterSelect>
      </div>

      {slots.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-8)',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No slots in this date range. Generate slots from an availability rule.
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '110px 60px 140px 1.2fr 1fr 100px 80px 80px 80px 100px 90px 1fr',
              gap: 'var(--sp-3)',
              padding: 'var(--sp-3) var(--sp-4)',
              borderBottom: '1px solid var(--border)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            <span>Date</span>
            <span>Day</span>
            <span>Time</span>
            <span>Provider</span>
            <span>Service</span>
            <span>Type</span>
            <span>Capacity</span>
            <span>Booked</span>
            <span>Remaining</span>
            <span>Status</span>
            <span>Source</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>
          {slots.map((s) => {
            const p = s.provider_id ? providerById.get(s.provider_id) : null;
            const svc = s.service_id ? serviceById.get(s.service_id) : null;
            const cap = s.capacity ?? 1;
            const booked = s.booked_count ?? 0;
            const remaining = Math.max(cap - booked, 0);
            const ds = deriveDisplayStatus(s.status, s.capacity, s.booked_count);
            const isBlocked = s.status === 'BLOCKED';
            const isCancelled = s.status === 'CANCELLED';
            const unused = booked === 0;
            return (
              <div
                key={s.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    '110px 60px 140px 1.2fr 1fr 100px 80px 80px 80px 100px 90px 1fr',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-3) var(--sp-4)',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 'var(--font-size-sm)',
                  alignItems: 'center',
                }}
              >
                <span>{s.slot_date}</span>
                <span>{dayShortFromIsoDate(s.slot_date)}</span>
                <span>
                  {formatTime(s.start_time)}–{formatTime(s.end_time)}
                </span>
                <span>{p?.display_name || p?.full_name || '—'}</span>
                <span>{svc?.name ?? '—'}</span>
                <span>{(s.slot_type ?? '').replace('_', ' ').toLowerCase()}</span>
                <span>{cap}</span>
                <span>{booked}</span>
                <span>{remaining}</span>
                <span>
                  <span
                    style={{
                      padding: '2px var(--sp-2)',
                      borderRadius: 'var(--radius-full)',
                      background: ds.bg,
                      color: ds.color,
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    {ds.label}
                  </span>
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {s.source}
                </span>
                <span
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-1)',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                  }}
                >
                  {!isBlocked && !isCancelled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void onSlotAction(s.id, 'block')}
                    >
                      Block
                    </Button>
                  )}
                  {isBlocked && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void onSlotAction(s.id, 'unblock')}
                    >
                      Unblock
                    </Button>
                  )}
                  {!isCancelled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void onSlotAction(s.id, 'cancel')}
                    >
                      Cancel
                    </Button>
                  )}
                  {unused && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void onSlotAction(s.id, 'delete')}
                    >
                      Delete
                    </Button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          height: 32,
          padding: '0 var(--sp-2)',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border-input)',
          background: 'var(--input-bg)',
          color: 'var(--text)',
          fontSize: 'var(--font-size-sm)',
        }}
      />
    </label>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 32,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border-input)',
        background: 'var(--input-bg)',
        color: 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        cursor: 'pointer',
      }}
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}
